import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

export const RATE_PLAN_ACTIVE_STATUS = 'content-approved';

export interface OfferRatePlan {
  id: string;
  name: string;
  rate_plan_code?: string | null;
  group?: { id: string; name: string } | null;
  default_plan?: boolean;
  schedules_list?: Array<unknown>;
  is_packaged_rate?: boolean;
  packaged_rate_id?: string | null;
  board_type?: string;
  created_at?: string;
  product_type?: string;
  discounts?: Array<unknown>;
  rate_type?: string;
  markup?: number;
  commission?: number;
  isActive: boolean;
  isUnassigned: boolean;
}

interface RoomType {
  id: string;
  room_rates?: Array<{ id: string; rate_plan: { id: string } }>;
}

interface Property {
  id: string;
  room_types?: Array<RoomType>;
}

interface PackageOption {
  fk_room_rate_id?: string;
}

interface Package {
  fk_property_id?: string;
  fk_room_rate_id?: string;
  package_options?: Array<PackageOption>;
}

interface Offer {
  status?: string;
  packages?: Array<Package>;
}

// Pages through every offer for the vendor account, same as the Vendor > Rate Plans page's fetchAllOffers.
const OFFERS_PAGE_LIMIT = 100;
const OFFERS_MAX_PAGES = 20;

async function getAllOffersForVendor(vendorAccountId: string): Promise<Array<Offer>> {
  const offers: Array<Offer> = [];

  for (let page = 1; page <= OFFERS_MAX_PAGES; page++) {
    const response = await OffersService.getOffers({
      page,
      limit: OFFERS_PAGE_LIMIT,
      queryString: vendorAccountId,
      orderByScheduleDate: 1,
    });
    const results: Array<Offer> = response?.result ?? [];
    offers.push(...results);
    if (results.length === 0) {
      break;
    }
  }

  return offers;
}

// Pages through every property for the vendor account, same as fetchAllOffers above — a single
// unpaginated page would silently drop room-rate mappings for any vendor with >1 page of properties.
const PROPERTIES_PAGE_LIMIT = 100;
const PROPERTIES_MAX_PAGES = 20;

async function getAllPropertiesForVendor(vendorAccountId: string): Promise<Array<Property>> {
  const properties: Array<Property> = [];

  for (let page = 1; page <= PROPERTIES_MAX_PAGES; page++) {
    const response = await ReservationService.getProperties(vendorAccountId, PROPERTIES_PAGE_LIMIT, page, true);
    const results: Array<Property> = response?.result ?? [];
    properties.push(...results);
    if (results.length < PROPERTIES_PAGE_LIMIT) {
      break;
    }
  }

  return properties;
}

export const offerRatePlanQueryKeys = {
  all: ['offerRatePlans'] as const,
  list: (vendorAccountId: string) => [...offerRatePlanQueryKeys.all, vendorAccountId] as const,
};

// All rate plans for the vendor, with isActive/isUnassigned matching the Vendor > Rate Plans page's
// vendor-wide scope — a rate plan sold on any property of this vendor counts, not just this offer's.
export function useGetOfferRatePlans(vendorAccountId: string | undefined) {
  return useQuery<Array<OfferRatePlan>>({
    queryKey: offerRatePlanQueryKeys.list(vendorAccountId ?? ''),
    queryFn: async () => {
      const [ratePlansResponse, properties, offers] = await Promise.all([
        ReservationService.getRatePlans(vendorAccountId),
        vendorAccountId ? getAllPropertiesForVendor(vendorAccountId) : Promise.resolve([]),
        vendorAccountId ? getAllOffersForVendor(vendorAccountId) : Promise.resolve([]),
      ]);

      const ratePlans: Array<OfferRatePlan> = ratePlansResponse.result ?? [];

      const roomRateToRatePlanId = new Map<string, string>();
      for (const property of properties) {
        for (const roomType of property.room_types ?? []) {
          for (const roomRate of roomType.room_rates ?? []) {
            if (roomRate.rate_plan?.id) {
              roomRateToRatePlanId.set(roomRate.id, roomRate.rate_plan.id);
            }
          }
        }
      }

      const ratePlanIdsWithOffer = new Set<string>();
      const activeRatePlanIds = new Set<string>();
      const addRatePlanForRoomRate = (roomRateId: string | undefined, offerStatus: string | undefined) => {
        const ratePlanId = roomRateId ? roomRateToRatePlanId.get(roomRateId) : undefined;
        if (!ratePlanId) {
          return;
        }
        ratePlanIdsWithOffer.add(ratePlanId);
        if (offerStatus === RATE_PLAN_ACTIVE_STATUS) {
          activeRatePlanIds.add(ratePlanId);
        }
      };
      for (const offer of offers) {
        for (const pkg of offer.packages ?? []) {
          for (const option of pkg.package_options ?? []) {
            addRatePlanForRoomRate(option.fk_room_rate_id, offer.status);
          }
        }
      }

      return ratePlans.map((ratePlan) => ({
        ...ratePlan,
        isActive: activeRatePlanIds.has(ratePlan.id),
        isUnassigned: !ratePlanIdsWithOffer.has(ratePlan.id),
      }));
    },
    enabled: !!vendorAccountId,
  });
}

export function useCloneRatePlan(vendorAccountId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = offerRatePlanQueryKeys.list(vendorAccountId ?? '');

  // Just clones and refetches, same as the Vendor > Rate Plans page — no room rate attachment.
  return useMutation({
    mutationFn: (ratePlanId: string) => ReservationService.cloneRatePlan(ratePlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useSetDefaultRatePlan(vendorAccountId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = offerRatePlanQueryKeys.list(vendorAccountId ?? '');

  return useMutation({
    mutationFn: (ratePlanId: string) => ReservationService.setDefaultRatePlan(ratePlanId),
    // Optimistic update so the radio flips immediately instead of waiting on a refetch.
    onMutate: async (ratePlanId: string) => {
      await queryClient.cancelQueries({ queryKey });
      const previousRatePlans = queryClient.getQueryData<Array<OfferRatePlan>>(queryKey);

      queryClient.setQueryData<Array<OfferRatePlan>>(queryKey, (ratePlans) =>
        ratePlans?.map((ratePlan) => ({ ...ratePlan, default_plan: ratePlan.id === ratePlanId })),
      );

      return { previousRatePlans };
    },
    onError: (_error, _ratePlanId, context) => {
      if (context?.previousRatePlans) {
        queryClient.setQueryData(queryKey, context.previousRatePlans);
      }
    },
    // Reconcile with the server in case it normalizes the default flip differently than our optimistic write.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
